import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { PlatformMessagesService } from '../../services/platform-messages.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-platform-messages-inbox',
  templateUrl: './platform-messages-inbox.component.html',
  styleUrls: ['./platform-messages-inbox.component.sass'],
})
export class PlatformMessagesInboxComponent implements OnInit, OnDestroy {
  private subs = new Subscription();

  messages: any[] = [];
  currentPage = 1;
  pageSize = 20;
  totalItems = 0;
  totalPages = 0;
  expandedId: number | null = null;
  unreadCount = 0;

  constructor(
    private messagesService: PlatformMessagesService,
    private socketService: SocketService,
    private spinner: SpinnerService,
  ) {}

  ngOnInit(): void {
    this.loadInbox();

    this.subs.add(
      this.socketService.onPlatformMessage$().subscribe((msg) => {
        this.messages.unshift({ ...msg, readAt: null });
        this.unreadCount++;
        this.totalItems++;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async loadInbox(): Promise<void> {
    const ref = this.spinner.start();
    try {
      const res = await this.messagesService.getInbox(
        this.currentPage,
        this.pageSize,
      );
      this.messages = res.data;
      this.totalItems = res.meta.total;
      this.totalPages = res.meta.totalPages;
      this.unreadCount = this.messages.filter((m: any) => !m.readAt).length;
    } catch (err) {
      console.error('Error loading inbox', err);
    } finally {
      this.spinner.stop(ref);
    }
  }

  async toggleMessage(msg: any): Promise<void> {
    if (this.expandedId === msg.id) {
      this.expandedId = null;
      return;
    }
    this.expandedId = msg.id;

    if (!msg.readAt) {
      try {
        const updated = await this.messagesService.markAsRead(msg.id);
        msg.readAt = updated.readAt;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.messagesService.decrementUnread();
      } catch (err) {
        console.error('Error marking as read', err);
      }
    }
  }

  async markAllAsRead(): Promise<void> {
    const ref = this.spinner.start();
    try {
      await this.messagesService.markAllAsRead();
      this.messages.forEach((m: any) => {
        if (!m.readAt) m.readAt = new Date().toISOString();
      });
      this.unreadCount = 0;
      this.messagesService.resetUnread();
    } catch (err) {
      console.error('Error marking all as read', err);
    } finally {
      this.spinner.stop(ref);
    }
  }

  onPageChange(delta: number): void {
    const next = this.currentPage + delta;
    if (next < 1 || next > this.totalPages) return;
    this.currentPage = next;
    this.loadInbox();
  }

  getTimeAgo(date: string): string {
    const diffMs = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m`;
    return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
